import { useQueries, useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useMemo } from 'react'

import { getRealtimeRef } from '@/integrations/firebase'

export type DocumentUploadStatus =
  | 'waiting_for_upload'
  | 'uploaded'
  | 'parsing'
  | 'analyzing'
  | 'embedding'
  | 'done'
  | 'error'

export const useDocumentUploadStatus = (individualId: string, documentIds: string[]) => {
  const results = useQueries({
    queries: documentIds.map(documentId => ({
      enabled: false,
      queryFn: () => Promise.resolve({ status: 'unknown' as DocumentUploadStatus }),
      queryKey: ['documentUploadStatus', individualId, documentId],
    })),
  })
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribes = documentIds.map(documentId => {
      const dbRef = getRealtimeRef(`individuals/${individualId}/documents/${documentId}`)

      return onValue(dbRef, snapshot => {
        if (!snapshot.exists()) return

        const data = snapshot.val()
        queryClient.setQueryData(['documentUploadStatus', individualId, documentId], data)
      })
    })

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe())
    }
  }, [individualId, documentIds, queryClient])

  const statuses = useMemo(() => {
    return results.reduce<Record<string, DocumentUploadStatus>>((acc, curr, i) => {
      if (curr.data) {
        const id = documentIds[i] ?? ''
        acc[id] = curr.data.status
      }

      return acc
    }, {})
  }, [results])

  return statuses
}
